<script setup lang="ts">
import { useAutoScroll } from "@/composition/useAutoScroll";
import { useColorResolver } from "@/composition/useColorResolver";
import { useEmitter } from "@/composition/useEmitter";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { LocationService } from "@/service/LocationServices";
import { useAppStore } from "@/store/app";
import {
  JUMP_TO_FIELDS,
  NewTemplateImage,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import {
  autoAddPrimaryField,
  handleContactFieldDrop,
  hasContactFields,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
  validateMixedCustomObjectTypes,
} from "@/util/customObjectUtils";
import { getCheckBoxStyles } from "@/util/default";
import {
  createBuilderScrollPreserver,
  preserveBuilderScrollPosition,
} from "@/util/focusNavigation";
import { getEffectiveBackgroundImageUrlForDevice } from "@/util/formStyleBackgroundImage";
import { localizeLegacyNames } from "@/util/localize";
import {
  addActiveTabLoopListener,
  applyBusinessStandardFieldPrefix,
  buildClonedSlideDataAndTasks,
  configureRatingElement,
  convertTextboxListOptionsForCustomObjectOnDrop,
  focusLabelAfterActiveChange,
  focusLabelFromUserEvent,
  getAllFontsImport,
  getConstructedQueryKey,
  getElementDOMUniqueId,
  normalizeContactStandardFieldDrop,
  removeActiveTabLoopListener,
  scrollContainerBySlideHeight,
} from "@/util/methods";
import {
  Copy01Icon,
  Settings02Icon,
  Trash04Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  renderIcon,
  UIButton,
  UICard,
  UITextMdMedium,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import { storeToRefs } from "pinia";
import uuid4 from "uuid4";
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import AlertModal from "../common/AlertModal.vue";
import BuilderBranding from "../common/BuilderBranding.vue";
import ContactAssociationSettingsModal from "../common/ContactAssociationSettingsModal.vue";
import CustomFieldsPreview from "../common/CustomFieldsPreview.vue";
import DefaultScreen from "../common/DefaultScreen.vue";
import Divider from "../common/Divider.vue";
import Footer from "../common/Footer.vue";
import HLImage from "../common/HLImage.vue";
import MixedCustomObjectErrorModal from "../common/MixedCustomObjectErrorModal.vue";
import StandardFieldPreview from "../common/StandardFieldPreview.vue";

const { t } = useI18n();
const store = useAppStore();
const builderCanvasBackgroundUrl = computed(() =>
  getEffectiveBackgroundImageUrlForDevice(store.formStyle, store.deviceMode)
);
const { normalizeHex } = useColorResolver(store);
const emitter = useEmitter();
const { elements } = storeToRefs(store);
const currentElement = ref({ label: "" });
const globalScriptCSSStyle = ref({ textContent: null });
const globalScriptCSSLabelHolder = ref({ textContent: null });
const globalScriptCSSPlaceHolder = ref({ textContent: null });
const globalScriptCSSFieldBGColor = ref({ textContent: null });
const globalScriptCSSFieldBorder = ref({ textContent: null });
const globalScriptCSSFieldShadow = ref({ textContent: null });
const globalScriptCSSFieldWidth = ref({ textContent: null });
const globalScriptCSSFieldPadding = ref({ textContent: null });
const globalScriptCSSFontHolder = ref({ textContent: null });

const globalScriptCSSShortLabelHolder = ref({ textContent: null });

// Auto-scroll during drag
const { handleDragStart, handleDragEnd } = useAutoScroll("form-builder");

const openAlertModal = ref(false);
const slideDeleteMessage = ref(t("quiz.deleteSlideMessage"));
const slideToBeRemoved = ref(undefined);
const preserveBuilderScroll = createBuilderScrollPreserver();

const formWidth = computed(() => {
  return store.deviceMode === "mobile" ? 400 : store.formStyle.width;
});

/** Header banner visibility (respects mobileHeaderImageSrcDeleted like form builder) */
const quizHeaderHasImage = computed(() => {
  const hasMobile =
    (store.formAction?.mobileHeaderImageSrc || "").trim().length > 0;
  const hasDesktop = (store.formAction?.headerImageSrc || "").trim().length > 0;
  const mobileDeleted = Boolean(store.formAction?.mobileHeaderImageSrcDeleted);
  if (store.deviceMode === "mobile") {
    return hasMobile || (!mobileDeleted && hasDesktop);
  }
  return hasDesktop;
});

const quizHeaderImageUrl = computed(() => {
  if (store.deviceMode === "mobile") {
    const mobileUrl = (store.formAction?.mobileHeaderImageSrc || "").trim();
    if (mobileUrl.length > 0) {
      return store.formAction?.mobileHeaderImageSrc || "";
    }
    if (store.formAction?.mobileHeaderImageSrcDeleted) {
      return "";
    }
    return store.formAction?.headerImageSrc || "";
  }
  return store.formAction?.headerImageSrc || "";
});

const progressBarWidth = computed(() => {
  return (slideIndex: number) => {
    const progress =
      slideIndex === store.slides.length - 1
        ? 95
        : ((slideIndex + 1) * 100) / store.slides.length;
    return `${progress}%`;
  };
});

const formStyle = computed(() => {
  const style = store.formStyle;
  // Use mobile padding if in mobile mode and it exists, otherwise fallback to desktop padding
  const padding =
    store.deviceMode === "mobile"
      ? style?.mobilePadding || style?.padding
      : style?.padding;
  const footerStyle =
    store.deviceMode === "mobile" ? store.mobileFooterStyle : store.footerStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;

  // Only apply bottom border radius if footer is not sticky
  const borderRadius = footerStyle?.stickyFooter
    ? `${style?.border?.radius}px ${style?.border?.radius}px 0 0`
    : `${style?.border?.radius}px`;

  // Calculate shadow extent (blur + spread)
  const shadowExtent =
    (style?.shadow?.blur || 0) + (style?.shadow?.spread || 0);

  return {
    padding: `${padding?.top || 0}px ${padding?.right || 0}px ${
      padding?.bottom || 0
    }px ${padding?.left || 0}px`,
    backgroundColor: `#${style?.background}`,
    border: `${style?.border?.border}px ${style?.border?.style} #${style?.border?.color}`,
    borderRadius: borderRadius,
    boxShadow: `${style?.shadow?.horizontal}px ${style?.shadow?.vertical}px ${
      style?.shadow?.blur
    }px ${style?.shadow?.spread}px #${normalizeHex(style?.shadow?.color)}`,
    width: `${formWidth.value}px`,
    clipPath: `inset(-${shadowExtent}px -${shadowExtent}px 0px -${shadowExtent}px)`,
    borderBottom: "none",
  };
});

const containerMarginTop = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const normalize = (v) =>
    v === "auto" || v === undefined || v === null ? 20 : v;

  return `${normalize(margin?.top)}px`;
});

const containerMarginBottom = computed(() => {
  const style = store.formStyle;
  const margin =
    store.deviceMode === "mobile"
      ? style?.mobileMargin || style?.margin
      : style?.margin;
  const normalize = (v) =>
    v === "auto" || v === undefined || v === null ? 20 : v;

  return `${normalize(margin?.bottom)}px`;
});

// Provide a stable identifier for elements that may not have uuid (legacy quizzes)
const getStableElementId = (el: any): string | undefined => {
  return el?.uuid || el?.hiddenFieldQueryKey || el?.fieldKey;
};

const clickedOnElement = async (e, element, elementId, slideId) => {
  await preserveBuilderScrollPosition(async () => {
    e.stopPropagation();
    // Fallback to a stable identifier when uuid is missing
    const idToUse = elementId || getStableElementId(element);
    const alreadyActive =
      store.currentActiveIndex[0] === slideId &&
      store.currentActiveIndex[1] === idToUse;

    if (!alreadyActive) {
      store.currentActiveIndex = [slideId, idToUse];
      store.currentSlideIndex = slideId;
    }

    if (
      !store.sidebarOverlaySettings ||
      store.sidebarOverlaySettings.elementId !== idToUse ||
      store.sidebarOverlaySettings.slideId !== slideId
    ) {
      store.sidebarOverlaySettings = {
        elementId: idToUse,
        slideId,
        formIndex: undefined,
      };
    }

    currentElement.value = element;
    await nextTick();
    if (!alreadyActive) {
      focusLabelFromUserEvent(e);
    }
  });
};

const fieldStyle = computed(() => {
  const fieldStyleStore = store.fieldStyle;
  const style = {
    padding: `${fieldStyleStore?.padding?.top}px ${fieldStyleStore?.padding?.right}px ${fieldStyleStore?.padding?.bottom}px ${fieldStyleStore?.padding?.left}px`,
    backgroundColor: "#" + fieldStyleStore?.bgColor,
    border: `${fieldStyleStore?.border?.border}px ${
      fieldStyleStore?.border?.type === "none"
        ? ""
        : fieldStyleStore?.border?.type
    } #${fieldStyleStore?.border?.color}`,
    borderRadius: fieldStyleStore?.border?.radius + "px",
    boxShadow: `${fieldStyleStore?.shadow?.horizontal}px ${
      fieldStyleStore?.shadow?.vertical
    }px ${fieldStyleStore?.shadow?.blur}px ${
      fieldStyleStore?.shadow?.spread
    }px #${normalizeHex(fieldStyleStore?.shadow?.color)}`,
    width: fieldStyleStore?.width + "px",
    color: "#" + fieldStyleStore?.labelColor,
    mobileLabelColor: "#" + fieldStyleStore?.mobileLabelColor,
    font: `${fieldStyleStore.labelFontSize}px  ${fieldStyleStore.labelFontWeight} ${fieldStyleStore.labelFontFamily}`,
  };
  return style;
});

onMounted(() => {
  const globalScriptCSS = document.createElement("style");
  globalScriptCSS.type = "text/css";

  // Google and bunny font css script
  const globalScriptFontFamily = document.createElement("style");
  globalScriptFontFamily.type = "text/css";
  globalScriptFontFamily.appendChild(document.createTextNode(""));
  globalScriptCSSFontHolder.value = globalScriptFontFamily.childNodes[0];
  document.head.appendChild(globalScriptFontFamily);

  for (let i = 0; i < 9; i++) {
    globalScriptCSS.appendChild(document.createTextNode(""));
  }
  globalScriptCSSStyle.value = globalScriptCSS.childNodes[0];
  //For Label CSS Change
  globalScriptCSSLabelHolder.value = globalScriptCSS.childNodes[1];
  //For Placeholder CSS Change
  globalScriptCSSPlaceHolder.value = globalScriptCSS.childNodes[2];
  globalScriptCSSFieldBGColor.value = globalScriptCSS.childNodes[3];
  globalScriptCSSFieldBorder.value = globalScriptCSS.childNodes[4];
  globalScriptCSSFieldShadow.value = globalScriptCSS.childNodes[5];
  globalScriptCSSFieldWidth.value = globalScriptCSS.childNodes[6];
  globalScriptCSSFieldPadding.value = globalScriptCSS.childNodes[7];
  globalScriptCSSShortLabelHolder.value = globalScriptCSS.childNodes[8];

  document.head.appendChild(globalScriptCSS);
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
  setGlobalStyleInHTMl();
  // Localize any legacy default page names like "Page N"
  localizeLegacyNames(store.slides, t, /^Page\s+\d+$/i, "quiz.pageLabel");
  // Ensure every quiz element has a unique uuid so selection targets a single element
  store.slides.forEach((slide) => {
    slide.slideData = slide.slideData.map((el) => {
      if (!el.uuid) {
        el.uuid = uuid4();
      }
      return el;
    });
  });
  addActiveTabLoopListener();
});

onBeforeUnmount(() => {
  removeActiveTabLoopListener();
});

// When the active element changes (via click, clone, or programmatic),
// try to auto-focus its label
watch(
  () => store.currentActiveIndex,
  async () => {
    await nextTick();
    focusLabelAfterActiveChange();
  },
  { deep: true }
);

const setGlobalStyleInHTMl = () => {
  const fieldStyle = store.fieldStyle;
  const formStyle = store.formStyle;
  const isMobile = store.deviceMode === "mobile";

  const getMobileOrDesktop = (mobileVal, desktopVal) =>
    isMobile ? mobileVal || desktopVal : desktopVal;

  const labelColor = getMobileOrDesktop(
    fieldStyle.mobileLabelColor,
    fieldStyle.labelColor
  );
  const labelFontSize = getMobileOrDesktop(
    fieldStyle.mobileLabelFontSize,
    fieldStyle.labelFontSize
  );
  const labelFontWeight = getMobileOrDesktop(
    fieldStyle.mobileLabelFontWeight,
    fieldStyle.labelFontWeight
  );
  const labelFontFamily = getMobileOrDesktop(
    fieldStyle.mobileLabelFontFamily,
    fieldStyle.labelFontFamily
  );

  const placeholderColor = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderColor,
    fieldStyle.placeholderColor
  );
  const placeholderFontFamily = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontFamily,
    fieldStyle.placeholderFontFamily
  );
  const placeholderFontSize = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontSize,
    fieldStyle.placeholderFontSize
  );
  const placeholderFontWeight = getMobileOrDesktop(
    fieldStyle.mobilePlaceholderFontWeight,
    fieldStyle.placeholderFontWeight
  );

  const shortLabelColor = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileColor,
    fieldStyle.shortLabel?.color
  );
  const shortLabelFontSize = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontSize,
    fieldStyle.shortLabel?.fontSize
  );
  const shortLabelFontWeight = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontWeight,
    fieldStyle.shortLabel?.fontWeight
  );
  const shortLabelFontFamily = getMobileOrDesktop(
    fieldStyle.shortLabel?.mobileFontFamily,
    fieldStyle.shortLabel?.fontFamily
  );

  globalScriptCSSLabelHolder.value.textContent = `#_builder-form label, #_builder-form .label-input {
      color: #${labelColor};
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
      font-family: '${labelFontFamily}';
      line-height: 1.8;
    }
    #_builder-form .tnc {
      font-family: '${labelFontFamily}';
    }
    #_builder-form pre {
      white-space: pre-wrap;
      word-break: break-word;
      max-width: 100%;
    }
    /* Bubble containers should inherit label typography */
    #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container {
      font-family: '${labelFontFamily}';
      font-size: ${labelFontSize}px;
      font-weight: ${labelFontWeight};
    }
    `;

  globalScriptCSSPlaceHolder.value.textContent = `#_builder-form ::placeholder, #_builder-form .form-dropdown a,
      #_builder-form .form-dropdown .dropdown-toggle, #_builder-form .form-placeholder , #_builder-form .currency-code, .input-icon { 
        color : #${placeholderColor}; 
        font-family:'${placeholderFontFamily}'; 
        font-size:${placeholderFontSize}px; 
        font-weight: ${placeholderFontWeight};
      }
      /* Bubble labels use placeholder color on label text */
      #_builder-form .bubble-label, #_builder-form .bubble-checkbox-label {
        color: #${placeholderColor} !important;
        background-color: #${fieldStyle.bgColor} !important;
        font-family:'${placeholderFontFamily}' !important;
        font-size:${placeholderFontSize}px !important;
        font-weight:${placeholderFontWeight} !important;
      }`;
  globalScriptCSSFieldBGColor.value.textContent = `#_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown{ background-color : #${fieldStyle.bgColor};}`;
  globalScriptCSSFieldBorder.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown , .form-input-border-style { border:" +
    fieldStyle.border.border +
    "px " +
    fieldStyle.border.type +
    " #" +
    fieldStyle.border.color +
    ";border-radius:" +
    fieldStyle.border.radius +
    "px ; }" +
    ` .line-form-style { border-bottom: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; background-color: #${fieldStyle.bgColor} !important;}` +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { border: ${fieldStyle.border.border}px ${fieldStyle.border.type} #${fieldStyle.border.color} !important; border-radius: 12px !important; }`;
  globalScriptCSSFieldPadding.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown , .input-form-padding-style { padding:" +
    fieldStyle.padding.top +
    "px " +
    fieldStyle.padding.right +
    "px " +
    fieldStyle.padding.bottom +
    "px " +
    fieldStyle.padding.left +
    "px}" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { padding: ${fieldStyle.padding.top}px ${fieldStyle.padding.right}px ${fieldStyle.padding.bottom}px ${fieldStyle.padding.left}px !important; background-clip: inherit !important; margin-left: 2px !important; margin-right: 2px !important; }`;
  const shadowColor = normalizeHex(fieldStyle.shadow?.color);
  globalScriptCSSFieldShadow.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown { box-shadow:" +
    fieldStyle.shadow.horizontal +
    "px " +
    fieldStyle.shadow.vertical +
    "px " +
    fieldStyle.shadow.blur +
    "px " +
    fieldStyle.shadow.spread +
    "px #" +
    shadowColor +
    " !important; }" +
    ` #_builder-form .bubble-container, #_builder-form .bubble-checkbox-container { box-shadow: ${fieldStyle.shadow.horizontal}px ${fieldStyle.shadow.vertical}px ${fieldStyle.shadow.blur}px ${fieldStyle.shadow.spread}px #${shadowColor}; }`;
  globalScriptCSSFieldWidth.value.textContent =
    "  #_builder-form .form-control, .form-control:disabled, .form-control[readonly], #_builder-form .form-dropdown {max-width:" +
    fieldStyle.width +
    "px}";
  globalScriptCSSShortLabelHolder.value.textContent = `#_builder-form .short-label, #_builder-form .short-label::placeholder {
      color: #${shortLabelColor} !important;
      font-size: ${shortLabelFontSize}px !important;
      font-weight: ${shortLabelFontWeight};
      font-family: '${shortLabelFontFamily}';
  }`;
  const styleStore = formStyle.customStyle.split("}");
  let styleString = "";
  styleStore.map(function (value: string) {
    if (value.includes("footer")) {
      styleString += `${value}}`;
    } else if (value !== "" && value.length > 0) {
      styleString += "#_builder-form " + value + "} ";
    }
  });

  globalScriptCSSStyle.value.textContent =
    styleString + getCheckBoxStyles(fieldStyle);
};

//Store Subscription to watch on store value change using patch
store.$subscribe(() => {
  setGlobalStyleInHTMl();
});

watch([store.fontCollection, () => store.builderConfig.isGDPRCompliant], () => {
  globalScriptCSSFontHolder.value.textContent = getAllFontsImport(
    store.fontCollection,
    store.builderConfig.isGDPRCompliant
  );
});

const updateElementProperty = (
  property: string,
  value: string,
  elementId: string
) => {
  if (!elementId) {
    return;
  }
  store.slides.forEach((slide) => {
    slide.slideData.forEach((element) => {
      const stableId = getStableElementId(element);
      if (stableId === elementId) {
        element[property] = value;
      }
    });
  });
};

const updateElementWithStableId = (
  element: Record<string, unknown>,
  value: any,
  updater: (newValue: any, elementId: string) => void
): void => {
  const stableId = getStableElementId(element);
  if (!stableId) {
    return;
  }
  updater(value, stableId);
};

const updateLabel = (newLabel: string, elementId: string) => {
  updateElementProperty("label", newLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updatePlaceholder = (newPlaceholder: string, elementId: string) => {
  updateElementProperty("placeholder", newPlaceholder, elementId);
  store.anyUnsavedChanges = true;
};

const updateShortLabel = (newShortLabel: string, elementId: string) => {
  updateElementProperty("shortLabel", newShortLabel, elementId);
  store.anyUnsavedChanges = true;
};

const updateLowestRating = (lowestRating: string, elementId: string) => {
  updateElementProperty("lowestRating", lowestRating, elementId);
  store.anyUnsavedChanges = true;
};

const updateHighestRating = (highestRating: string, elementId: string) => {
  updateElementProperty("highestRating", highestRating, elementId);
  store.anyUnsavedChanges = true;
};

const isActiveElement = (slideId, element) => {
  const id = getStableElementId(element);
  return (
    store.currentActiveIndex[0] === slideId &&
    store.currentActiveIndex[1] === id
  );
};

const ChangeInSlideData = async (e, slideIndex, slideId) => {
  await preserveBuilderScrollPosition(
    () => {
      const item = e.added?.element || e.moved?.element || e.removed?.element;
      let elementDrop = {
        ...cloneDeep(item),
        hiddenFieldQueryKey: item.id ? item.hiddenFieldQueryKey : item.tag,
        active: false,
      };
      // Centralized normalization for standard drops
      elementDrop = applyBusinessStandardFieldPrefix(elementDrop);
      elementDrop = normalizeContactStandardFieldDrop(elementDrop);
      if (e.added) {
        setTimeout(() => {
          if (
            store.slides[slideIndex].slideData.filter(
              (element) => element.model === "opportunity"
            )?.length === 1
          ) {
            store.showOpportunitiesModal = true;
          }

          // Trigger contact association modal when contact field is dropped and custom objects exist
          handleContactFieldDrop(elementDrop, store, {
            slides: store.slides,
          });

          // Trigger contact association modal for true custom objects (exclude business/opportunity)
          if (
            store.slides[slideIndex].slideData.filter((element) =>
              isTrueCustomObjectField(element.model)
            )?.length === 1 &&
            !store.contactAssociationSettings &&
            hasContactFields(null, store.slides) && // Only show modal if there are contact fields
            !store.slides
              .flatMap((s) => s.slideData || [])
              .some((e) => e.model === "business" || e.model === "opportunity")
          ) {
            // Find the custom objects element and store its model
            const customObjectElement = store.slides[slideIndex].slideData.find(
              (element) => isTrueCustomObjectField(element.model)
            );
            if (customObjectElement) {
              store.currentCustomObjectModel = customObjectElement.model;
              store.showContactAssociationModal = true;
            }
          }
          // If this is the first associated object field, add primary field automatically
          else if (
            store.slides[slideIndex].slideData.filter((element) =>
              isCustomObjectField(element.model)
            )?.length === 1 &&
            !store.contactAssociationSettings &&
            (!hasContactFields(null, store.slides) ||
              elementDrop.model === "business")
          ) {
            // Auto-add primary field without showing modal
            autoAddPrimaryField(elementDrop.model, store, {
              slides: store.slides,
              slideIndex: slideIndex,
              addedIndex: e.added.newIndex,
            });
          }

          // Validation for mixed custom object types
          const validationResult = validateMixedCustomObjectTypes(
            elementDrop,
            null, // elements (not used for quizzes)
            store.slides,
            slideIndex,
            e.added.newIndex,
            store.customObjects || [],
            t
          );

          if (!validationResult.isValid && validationResult.errorInfo) {
            store.mixedCustomObjectErrorInfo =
              validationResult.errorInfo as any;
            store.showMixedCustomObjectErrorModal = true;
            // Exit early to prevent further processing
          }
        }, 1000);
        setTimeout(async () => {
          let isAlreadyPresent = false;
          let customElementCount = 0;
          store.slides.forEach((slide) => {
            slide.slideData.forEach((element) => {
              const isSameId =
                elementDrop?.id && element?.id === elementDrop?.id;
              const isSameTag =
                !elementDrop?.id && element.tag === elementDrop.tag;
              const isCustomOrDuplicateAllowed =
                elementDrop?.custom ||
                store.duplicateAllowed.includes(elementDrop.tag);
              if ((isSameId || isSameTag) && !isCustomOrDuplicateAllowed) {
                isAlreadyPresent = true;
              }
              if (element.custom) {
                customElementCount += 1;
              }
            });
          });
          if (!isAlreadyPresent && e.added) {
            // Only generate a new label/key for custom fields when they are added from the sidebar
            // Detect sidebar-add by absence of an existing uuid (elements on slides have uuid)
            const isSidebarAdd = !elementDrop?.uuid;
            const isCustomField =
              elementDrop?.custom || elementDrop?.id !== undefined;
            const isBrandNewCustom =
              isCustomField &&
              isSidebarAdd &&
              !elementDrop?.id &&
              !elementDrop?.fieldKey;
            if (isBrandNewCustom) {
              if (!store.parentFolderId) {
                store.parentFolderName = `Quiz | ${store.builder.name}`;
                const response = await LocationService.createCustomField(
                  store.locationId,
                  {
                    name: store.parentFolderName,
                    documentType: "folder",
                    model: "contact",
                  }
                );
                store.parentFolderId = response.data?.customFieldFolder?.id;
              }
              const uniqueKey = UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
                ? UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
                : "";
              const existingElementsCount = [
                ...store.customFields,
                ...store.slides[slideIndex].slideData,
              ].filter(
                (element) => element.dataType === elementDrop.dataType
              )?.length;
              elementDrop.label = `${elementDrop.label}${
                uniqueKey ? ` ${uniqueKey} ` : " "
              }${existingElementsCount + 1}${Math.random()
                .toString(36)
                .substr(2, 3)}`;
              elementDrop.hiddenFieldQueryKey = elementDrop.hiddenFieldQueryKey
                ? elementDrop.hiddenFieldQueryKey
                : getConstructedQueryKey(elementDrop.label);
              elementDrop.parentId = store.parentFolderId;
            }
            delete elementDrop.icon;
            delete elementDrop.category;
            elementDrop.uuid = uuid4();
            // To configure rating element to add default rating properties for rating element coming from custom fields
            elementDrop = configureRatingElement(elementDrop);
            const isJumpToFieldsExists = store.slides[
              slideIndex
            ].slideData.filter((field) =>
              JUMP_TO_FIELDS.includes(field.dataType)
            );
            let targetSlideIndex = slideIndex;
            const targetElementIndex = e.added.newIndex;
            let insertedElement: any = null;
            if (
              isJumpToFieldsExists.length >= 1 &&
              JUMP_TO_FIELDS.includes(elementDrop.dataType)
            ) {
              const nextSlideIndex = slideIndex;
              addSlideBelow(slideIndex);
              convertTextboxListOptionsForCustomObjectOnDrop(
                elementDrop,
                isCustomObjectField(elementDrop?.model)
              );
              targetSlideIndex = nextSlideIndex + 1;
              store.slides[targetSlideIndex].slideData.splice(
                targetElementIndex,
                0,
                elementDrop
              );
              insertedElement =
                store.slides[targetSlideIndex].slideData[targetElementIndex];
            } else {
              convertTextboxListOptionsForCustomObjectOnDrop(
                elementDrop,
                isCustomObjectField(elementDrop?.model)
              );
              // If this is a primary field, make it required by default
              const maybePrimary = isPrimaryField(
                elementDrop,
                store.customObjects || []
              );
              if (maybePrimary.isPrimary) {
                elementDrop.required = true;
              }
              store.slides[slideIndex].slideData.splice(
                targetElementIndex,
                0,
                elementDrop
              );
              insertedElement =
                store.slides[slideIndex].slideData[targetElementIndex];
            }

            if (
              elementDrop.model === "opportunity" &&
              store.opportunitySettings.payload.customFields.length
            ) {
              store.opportunitySettings.payload.customFields.push({
                id: elementDrop.Id,
                key: elementDrop.fieldKey.replace("opportunity.", ""),
              });
            }

            if (insertedElement) {
              const stableId =
                insertedElement.uuid || insertedElement.hiddenFieldQueryKey;
              const targetSlide = store.slides[targetSlideIndex];
              const targetSlideId = targetSlide?.id ?? targetSlideIndex;
              store.currentActiveIndex = [
                targetSlideId,
                stableId || targetElementIndex,
              ];
              store.currentSlideIndex = targetSlideId;
              store.sidebarOverlaySettings = {
                elementId: stableId || getStableElementId(insertedElement),
                slideId: targetSlideId,
                formIndex: undefined,
              };
              store.isAnyElementActive = true;
            }
          }
          store.addToHistory();
        }, 200);
      }

      if (e.removed) {
        store.slides[slideIndex].slideData.splice(e.removed.oldIndex, 1);
        store.addToHistory();
      }
      if (e.moved) {
        // Move - Handle drag and drop reordering
        const draggedElement =
          store.slides[slideIndex].slideData[e.moved.oldIndex];

        // Handle the case where we're moving to the same position (no-op)
        if (e.moved.oldIndex === e.moved.newIndex) {
          return;
        }
        const newSlideData = [...store.slides[slideIndex].slideData];
        newSlideData.splice(e.moved.oldIndex, 1);
        newSlideData.splice(e.moved.newIndex, 0, draggedElement);
        store.slides[slideIndex].slideData = newSlideData;

        // If the moved element is currently active in the sidebar, refresh the sidebarOverlaySettings
        // to ensure the sidebar re-binds to the element in its new position
        const draggedElementUuid = getStableElementId(draggedElement);
        if (
          store.sidebarOverlaySettings?.elementId === draggedElementUuid &&
          store.sidebarOverlaySettings?.slideId === slideId
        ) {
          // Refresh the sidebar settings with the same IDs to trigger re-binding
          store.sidebarOverlaySettings = {
            elementId: draggedElementUuid,
            slideId: slideId,
            formIndex: undefined,
          };
        }

        store.addToHistory();
      }
      store.anyUnsavedChanges = true;
    },
    { restoreDelays: [0, 100, 300, 800, 1200], lockDurationMs: 1300 }
  );
};

const addSlideBelow = (index) => {
  const newSlide = {
    slideName: getSuccessorIndex(),
    slideData: [],
    id: uuid4(),
  };
  store.slides.splice(index + 1, 0, newSlide);
  store.formAction.fieldPerPage = store.slides.length;
  store.anyUnsavedChanges = true;
  emitter.emit("open-left-side-bar");
};

const slideChange = (e) => {
  allSlideInactive();
  store.isAnyElementActive = false;
  store.anyUnsavedChanges = true;
};

const getSuccessorIndex = () => {
  return t("quiz.pageLabel", { n: store.slides.length + 1 });
};

const allSlideInactive = () => {
  store.currentSlideIndex = undefined;
  store.currentActiveIndex = [undefined, undefined];
};

const cloneSlide = (slideIndex: number) => {
  const sourceSlide = store.slides[slideIndex];
  if (!sourceSlide) {
    return;
  }

  // Only clone elements whose tag is allowed to be duplicated for standard fields;
  // always keep custom fields
  const allowedTags = Array.isArray(store.duplicateAllowed)
    ? store.duplicateAllowed
    : [];
  const filteredSlide = {
    ...sourceSlide,
    // Keep only cloneable elements:
    // - Standard fields that are allowed
    // - Custom fields with model === contact or no model
    slideData: (sourceSlide.slideData || []).filter((el: any) => {
      const isCustom = el?.id !== undefined || el?.custom === true;
      if (isCustom) {
        return !el?.model || el?.model === "contact";
      }
      return allowedTags.includes(el?.tag);
    }),
  } as any;

  const { clonedSlideData, runCustomFieldCreations } =
    buildClonedSlideDataAndTasks(filteredSlide, slideIndex, store);

  const newSlide = {
    ...cloneDeep(sourceSlide),
    id: uuid4(),
    slideName: `${sourceSlide.slideName} (copy)`,
    slideData: clonedSlideData,
  };

  store.slides.splice(slideIndex + 1, 0, newSlide);
  store.anyUnsavedChanges = true;
  store.addToHistory();
  // Since new slide is inserted immediately after, scroll by next slide height minus 20px
  scrollContainerBySlideHeight(
    "form-builder",
    "form-builder-container",
    ".survey-form-container",
    slideIndex + 1,
    20
  );
};

const removeSlide = (slideIndex) => {
  openAlertModal.value = true;
  slideToBeRemoved.value = slideIndex;
  if (slideIndex < store.slides.length - 1) {
    slideDeleteMessage.value = `${t("survey.slideDeleteBetweenAlertMessage")}`;
  } else {
    slideDeleteMessage.value = `${t("survey.slideDeleteAlertMessage")}`;
  }
};

const openSlideSettings = (slideId) => {
  store.sidebarOverlaySettings = {
    elementId: undefined,
    slideId: slideId,
    formIndex: undefined,
  };
  store.isAnyElementActive = true;
};

const feedback = (modalName, result) => {
  if (result) {
    store.slides.splice(slideToBeRemoved.value, 1);
    store.anyUnsavedChanges = true;
  }
  slideToBeRemoved.value = undefined;
  openAlertModal.value = false;
};
const popModalChange = () => {
  openAlertModal.value = false;
};

const options = computed(() => {
  return [
    {
      label: `${t("quiz.settings")}`,
      key: "settings",
      icon() {
        return h(renderIcon(Settings02Icon), null);
      },
    },
    {
      label: `${t("quiz.deletePage")}`,
      key: "delete",
      disabled: store.slides.length === 1,
      icon() {
        return h(renderIcon(Trash04Icon), { style: { color: "red" } });
      },
    },
    {
      label: `${t("quiz.clonePage")}`,
      key: "clone",
      icon() {
        return h(renderIcon(Copy01Icon), null);
      },
    },
  ];
});

const actionON = (key, slideId, slideIndex) => {
  if (key === "settings") {
    openSlideSettings(slideId);
  }
  if (key === "delete") {
    removeSlide(slideIndex);
  }
  if (key === "clone") {
    cloneSlide(slideIndex);
  }
};

const clickedOutSideSlide = (e) => {
  store.currentCustomSidebarPreview = undefined;
  store.sidebarOverlaySettings = undefined;
  store.isAnyElementActive = false;
  allSlideInactive();
};

const applyInlineStyle = () => {
  return {
    marginBottom: store.formStyle.fieldSpacing + "px",
  };
};

/**
 * Handle contact association settings save
 */
const saveContactAssociationSettings = (settings) => {
  store.contactAssociationSettings = settings;
  store.anyUnsavedChanges = true;
  store.showContactAssociationModal = false;
};

/**
 * Handle contact association settings cancel
 */
const cancelContactAssociationSettings = () => {
  store.showContactAssociationModal = false;
};

const shouldRemoveExtraPadding = computed(() => {
  const padding =
    store.deviceMode === "mobile"
      ? store.formStyle.mobilePadding
      : store.formStyle.padding;
  return padding?.extraPaddingComputingProcessed === true;
});
</script>

<template>
  <section
    class="hl_wrapper d-flex survey-wrap-container"
    @click.prevent="clickedOutSideSlide"
  >
    <section
      id="form-builder"
      class="hl_wrapper--inner form-builder"
      :style="{
        backgroundImage: `url(${builderCanvasBackgroundUrl})`,
        backgroundSize: 'cover',
      }"
      @mousedown.capture="preserveBuilderScroll"
      @focusin.capture="preserveBuilderScroll"
    >
      <section
        class="hl_form-builder--main"
        :style="{
          marginTop: containerMarginTop,
          marginBottom: containerMarginBottom,
        }"
      >
        <form id="_builder-form" name="builder-form" class="builder-preview">
          <div class="form-child-wrapper">
            <div>
              <VueDraggableNext
                id="form-builder-container"
                :list="store.slides"
                :sort="true"
                group="slide"
                :animation="300"
                @change="slideChange"
              >
                <div
                  v-for="(itemSlide, indexSlide) in store.slides"
                  :key="indexSlide"
                  class="cursor-pointer"
                >
                  <div
                    class="survey-form-container flex"
                    :style="{
                      paddingBottom:
                        indexSlide === store.slides.length - 1 ? '200px' : 0,
                    }"
                  >
                    <div class="builder-form-name">
                      <div
                        class="mb-1 flex h-10 items-center justify-between justify-items-center text-center"
                      >
                        <div class="flex items-center text-sm font-semibold">
                          {{ itemSlide?.slideName }}
                        </div>
                        <div class="ml-2 flex items-center gap-2">
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Settings02Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="openSlideSettings(itemSlide.id)"
                              />
                            </template>
                            {{ t("survey.settings") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Copy01Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                @mousedown.stop
                                @click.stop="cloneSlide(indexSlide)"
                              />
                            </template>
                            {{ t("survey.cloneSlide") }}
                          </UITooltip>
                          <UITooltip trigger="hover" placement="top">
                            <template #trigger>
                              <Trash04Icon
                                class="focus_outline_none h-5 w-5 cursor-pointer text-gray-900 ring-0 hover:text-gray-700 focus:outline-none focus:ring-0 active:outline-none"
                                :class="{
                                  'cursor-not-allowed opacity-50':
                                    store.slides.length === 1,
                                }"
                                @mousedown.stop
                                @click.stop="
                                  store.slides.length === 1
                                    ? null
                                    : removeSlide(indexSlide)
                                "
                              />
                            </template>
                            {{ t("survey.delete") }}
                          </UITooltip>
                        </div>
                      </div>
                      <!-- Mobile Progress Bar -->
                      <div
                        v-if="
                          store.deviceMode === 'mobile' &&
                          store.mobileFooterStyle?.enableProgressBar
                        "
                        class="mobile-progress-bar-overlay"
                        :style="{
                          backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.inactiveFillColor}`,
                          maxWidth: formWidth + 'px',
                        }"
                      >
                        <div
                          class="mobile-progress-fill"
                          :style="{
                            width: progressBarWidth(indexSlide),
                            backgroundColor: `#${store.mobileFooterStyle.progressBarStyle.completeFillColor}`,
                          }"
                        ></div>
                      </div>
                      <div
                        v-if="quizHeaderHasImage"
                        class="header-image-container"
                      >
                        <div
                          class="ghl-header-banner w-full max-w-full"
                          :style="{
                            maxWidth: formWidth + 'px',
                          }"
                        >
                          <HLImage
                            :url="quizHeaderImageUrl"
                            :width="store.formStyle.width"
                            :alt="t('common.bannerAlt')"
                          ></HLImage>
                        </div>
                      </div>
                      <div
                        id="the-form-builder"
                        class="form-element form-builder--wrap"
                        :style="formStyle"
                      >
                        <div
                          :class="`drop ${
                            shouldRemoveExtraPadding
                              ? ''
                              : 'builder-padding-remove'
                          }`"
                        >
                          <VueDraggableNext
                            :model-value="itemSlide.slideData"
                            class="builder_sub_container"
                            :class="store.builderConfig.inlineForm ? 'row' : ''"
                            group="elements"
                            :sort="true"
                            :animation="300"
                            handle=".field-draggable-area"
                            ghost-class="field-drag-ghost"
                            @start="handleDragStart"
                            @end="handleDragEnd"
                            @change="
                              (e) =>
                                ChangeInSlideData(e, indexSlide, itemSlide.id)
                            "
                          >
                            <div
                              v-for="(element, index) in itemSlide.slideData"
                              :key="
                                getElementDOMUniqueId(
                                  'quiz-element',
                                  element,
                                  index,
                                  itemSlide.id
                                )
                              "
                              class="smooth-dnd-draggable-wrapper element-box menu-field-wrap col-12"
                              :style="applyInlineStyle()"
                              @click="
                                (e) =>
                                  clickedOnElement(
                                    e,
                                    element,
                                    element.uuid,
                                    itemSlide.id
                                  )
                              "
                            >
                              <div
                                :class="{
                                  active: isActiveElement(
                                    itemSlide.id,
                                    element.uuid
                                  ),
                                }"
                                class="menu-item"
                              >
                                <StandardFieldPreview
                                  v-if="
                                    !(
                                      element.id !== undefined ||
                                      element?.custom
                                    )
                                  "
                                  :key="
                                    getElementDOMUniqueId(
                                      'std-field',
                                      element,
                                      index,
                                      itemSlide.id
                                    )
                                  "
                                  :element="element"
                                  :index="index"
                                  :slide-index="indexSlide"
                                  :slide="itemSlide"
                                  :current-element="currentElement"
                                  :field-style="fieldStyle"
                                  :form-label-visible="
                                    store.builderConfig.formLabelVisible
                                  "
                                  :media-center-sub-category="
                                    MediaCenterSubCategory.QUIZ
                                  "
                                  @update:label="
                                    (label) =>
                                      updateElementWithStableId(
                                        element,
                                        label,
                                        updateLabel
                                      )
                                  "
                                  @update:placeholder="
                                    (placeholder) =>
                                      updateElementWithStableId(
                                        element,
                                        placeholder,
                                        updatePlaceholder
                                      )
                                  "
                                  @update:short-label="
                                    (shortLabel) =>
                                      updateElementWithStableId(
                                        element,
                                        shortLabel,
                                        updateShortLabel
                                      )
                                  "
                                ></StandardFieldPreview>
                                <CustomFieldsPreview
                                  v-if="
                                    element.id !== undefined || element?.custom
                                  "
                                  :key="
                                    getElementDOMUniqueId(
                                      'custom-field',
                                      element,
                                      index,
                                      itemSlide.id
                                    )
                                  "
                                  :element="element"
                                  :index="index"
                                  :slide-index="indexSlide"
                                  :slide="itemSlide"
                                  :current-element="currentElement"
                                  :field-style="fieldStyle"
                                  :form-label-visible="
                                    store.builderConfig.formLabelVisible
                                  "
                                  :is-form="false"
                                  @update:label="
                                    (label) =>
                                      updateElementWithStableId(
                                        element,
                                        label,
                                        updateLabel
                                      )
                                  "
                                  @update:placeholder="
                                    (placeholder) =>
                                      updateElementWithStableId(
                                        element,
                                        placeholder,
                                        updatePlaceholder
                                      )
                                  "
                                  @update:short-label="
                                    (shortLabel) =>
                                      updateElementWithStableId(
                                        element,
                                        shortLabel,
                                        updateShortLabel
                                      )
                                  "
                                  @update:lowest-rating="
                                    (lowestRating) =>
                                      updateElementWithStableId(
                                        element,
                                        lowestRating,
                                        updateLowestRating
                                      )
                                  "
                                  @update:highest-rating="
                                    (highestRating) =>
                                      updateElementWithStableId(
                                        element,
                                        highestRating,
                                        updateHighestRating
                                      )
                                  "
                                ></CustomFieldsPreview>
                              </div>
                            </div>

                            <DefaultScreen
                              v-if="!itemSlide?.slideData?.length"
                              :add-slide-text="t('survey.addSlideText')"
                              :page-index="indexSlide"
                            />
                          </VueDraggableNext>
                          <BuilderBranding
                            :is-branding-active="store.formStyle.acBranding"
                            :company="store.company"
                          />
                        </div>
                      </div>
                      <div>
                        <Footer
                          v-if="store.footerStyle.stickyFooter"
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="
                            indexSlide === store.slides.length - 1
                          "
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                        <Footer
                          v-if="
                            !store.footerStyle.stickyFooter &&
                            store.deviceMode === 'mobile'
                          "
                          :current-slide-index="indexSlide"
                          :is-last-slide-index="
                            indexSlide === store.slides.length - 1
                          "
                          :style="{ maxWidth: formWidth + 'px' }"
                        />
                      </div>
                      <section class="flex justify-center">
                        <div
                          :style="{ maxWidth: formWidth + 'px' }"
                          class="w-full"
                        >
                          <Divider
                            :add-slide-text="t('quiz.addNewPage')"
                            @add-slide="() => addSlideBelow(indexSlide)"
                          />
                        </div>
                      </section>

                      <section
                        v-if="indexSlide === store.slides.length - 1"
                        class="mt-4 w-full"
                      >
                        <UICard id="edit-template-card">
                          <div class="flex gap-x-4">
                            <img
                              :src="NewTemplateImage"
                              class="h-[80px] w-[124px] shrink-0 rounded"
                              :alt="t('quiz.quizResult.resultTemplateAlt')"
                            />
                            <div>
                              <UITextMdMedium class="mb-2 text-gray-900">{{
                                t("quiz.quizResult.resultTemplate")
                              }}</UITextMdMedium>
                              <UIButton
                                id="form_builder-btn-edit_quiz_result"
                                type="default"
                                @click="store.openQuizResultBuilderModal"
                                >{{ t("quiz.editTemplate") }}</UIButton
                              >
                            </div>
                          </div>
                        </UICard>
                      </section>
                    </div>
                  </div>
                </div>
              </VueDraggableNext>
            </div>
          </div>
        </form>
      </section>
    </section>
    <Footer
      v-if="!store.footerStyle.stickyFooter && store.deviceMode === 'desktop'"
    />
  </section>
  <AlertModal
    v-if="openAlertModal"
    :title="t('common.alert')"
    :description="slideDeleteMessage"
    modal-name="slideDelete"
    :show-modal-popup="openAlertModal"
    :positive-text="t('common.ok')"
    :negative-text="t('common.cancel')"
    header-type="error"
    footer-type="primary"
    @pop:change="popModalChange"
    @pop:positive="feedback"
    @pop:negative="feedback"
  ></AlertModal>
  <ContactAssociationSettingsModal
    :show="store.showContactAssociationModal"
    :object-model="store.currentCustomObjectModel"
    @update:success="saveContactAssociationSettings"
    @update:cancel="cancelContactAssociationSettings"
  />
  <MixedCustomObjectErrorModal
    :show="store.showMixedCustomObjectErrorModal"
    :existing-object-name="store.mixedCustomObjectErrorInfo.existingObjectName"
    :attempted-object-name="
      store.mixedCustomObjectErrorInfo.attemptedObjectName
    "
    @update:close="store.showMixedCustomObjectErrorModal = false"
  />
</template>

<style scoped>
.settings {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 5px;
  border-radius: 5px;
  background-color: #007bff;
  color: #fff;
  border-color: #007bff;
  z-index: 100;
  position: absolute;
  margin-bottom: 10px;
}

.form-control {
  border-radius: 0.3125rem;
  padding: 15px 20px;
  font-size: 0.875rem;
}
.form-control:disabled,
.form-control [readonly] {
  background-color: #e9ecef;
  opacity: 1;
}

#settings {
  font-size: 10px;
  height: 30px;
}

.builder-form-name {
  margin: 0 auto;
}

.icon-settings {
  display: flex;
  align-items: center;
}

.icon-settings svg {
  padding-right: 5px;
}

.builder-preview {
  min-height: 450px;
  margin: 0 auto;
  width: 100%;
}

#form-builder {
  background-color: #f3f4f6;
  margin-bottom: 0px;
  overflow: auto;
  margin: 0 auto;
  height: calc(100vh - 52px);
  width: 100%;
}

.add_new_slides {
  text-align: center;
  margin-bottom: 20px;
}

.builder-item {
  cursor: pointer;
  width: 100%;
  background: grey;
  margin: 0.5rem 0;
  min-height: 3rem;
}

.builder_sub_container {
  min-height: 140px;
}

#_builder-form .active {
  background: transparent;
  cursor: move;
}

.smooth-dnd-draggable-wrapper {
  cursor: move;
}

.field-drag-ghost {
  opacity: 1 !important;
  background-color: #f2f4f7 !important;
  border-radius: 4px;
  min-height: 60px;
  position: relative;
}

.field-drag-ghost * {
  visibility: hidden !important;
}

#_builder-form .main_active {
  border: 1px solid #168bf5;
}

.form-builder--wrap {
  border-radius: 4px;
  border: 1px dashed #cde0ec;
  padding: 30px 15px;
  background-color: #fff;
  position: relative;
}

.survey-wrap-container .survey-form-container {
  padding-top: 10px;
  padding-bottom: 10px;
  position: relative;
}

.form-builder--item {
  margin-bottom: 15px;
  border-radius: 4px;
  border: 2px solid transparent;
  -webkit-transition: border 0.2s ease-in-out;
  -o-transition: border 0.2s ease-in-out;
  transition: border 0.2s ease-in-out;
  position: relative;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-even
  .form-builder--item {
  padding-left: 4px !important;
}

.builder-inline
  .smooth-dnd-draggable-wrapper.col-md-6
  .f-odd
  .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.builder-inline .smooth-dnd-draggable-wrapper.col-12 .form-builder--item {
  padding-left: 10px;
  padding-right: 4px;
}

.form-builder--item h1,
.form-builder--item h2,
.form-builder--item h3,
.form-builder--item h4,
.form-builder--item h5,
.form-builder--item h6 {
  margin-bottom: 0;
}

.form-builder--item label {
  font-size: 0.875rem;
  color: #2a3135;
}

.captcha-wrap-img {
  width: 335px;
  display: block;
}

.button-element {
  min-width: 85px;
}

.menu-item {
  position: relative;
}

.menu-field-wrap {
  position: relative;
}

/* the-form-builder styles consolidated */

.builder-padding-remove {
  padding: 30px 0;
}

/* Scope dropdown option suffix to this component via id namespace */
[id^="quiz-dropdown-"] .n-dropdown-option-body__suffix {
  display: flex !important;
}

.add-dropzone-builder {
  background: transparent;
  width: 100%;
  height: 86px;
  border-width: 1;
  border-style: solid;
  border-color: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #6c757d !important;
}

.add-dropzone-builder p {
  margin-left: 5px !important;
}

#form-builder-container {
  height: 100%;
  min-height: 340px;
}

.slide_settings {
  position: absolute;
  right: 130px;
  top: 10px;
}

.survey-form-container a.close-icon-survey {
  position: absolute;
  right: 0px;
  top: 0px;
  -webkit-transition: all 0.5s ease-in;
  transition: all 0.5s ease-in;
  font-size: 10px;
  /* padding: 5px 7px; */
  background: #f5f5f5;
  color: #2a3135;
  border-radius: 50%;
  height: 20px;
  cursor: pointer;
}
.focus_outline_none:focus {
  outline: none;
}
.ghl-header-banner {
  margin: 0 auto;
}

.element-box {
  display: flex;
  flex-flow: column;
  height: 100%;
}

.slide-header {
  border-bottom: 1px solid #eaecf0;
}

.header-image-container {
  position: relative;
  margin: 0 auto;
}

.mobile-progress-bar-overlay {
  position: relative;
  height: 8px;
  border-radius: 4px;
  z-index: 20;
  margin: 0 auto;
}

.mobile-progress-fill {
  height: 100%;
  border-radius: 4px 0 0 0;
  transition: width 0.3s ease-in-out;
}

:deep(.hl-card .hl-card-content) {
  padding: 16px !important;
}
</style>
